import { useI18n } from '../../contexts/I18nContext';
import { mediaLabel, type WhatsAppMessage } from '../../lib/whatsappApi';
import { groupTranscript } from '../../lib/whatsappTranscript';
import { Avatar } from '../tasks/linear/Avatar';

/**
 * Read-only WhatsApp group transcript (client page): a separator per day, and
 * consecutive messages from one sender folded under one header (grouping in
 * lib/whatsappTranscript.ts). Media shows as a label; the files themselves are
 * not synced.
 */
export function WhatsAppTranscript({ messages }: { messages: WhatsAppMessage[] }) {
  const { locale } = useI18n();
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
  const days = groupTranscript(messages, locale);

  return (
    <div className="space-y-2">
      {days.map((day) => (
        <section key={day.key}>
          <div className="my-4 flex items-center gap-3">
            <span className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium text-muted-foreground">{day.label}</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <ol className="space-y-4">
            {day.runs.map((run) => (
              <li key={run.key} className="flex gap-3">
                <Avatar name={run.senderName} size={28} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`truncate text-sm font-semibold ${
                        run.isFromMe ? 'text-brand-600' : 'text-foreground'
                      }`}
                    >
                      {run.senderName}
                    </span>
                    <time
                      dateTime={run.messages[0].sentAt}
                      className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
                    >
                      {time.format(new Date(run.messages[0].sentAt))}
                    </time>
                  </div>
                  <ol className="mt-0.5 space-y-1">
                    {run.messages.map((m) => {
                      const media = mediaLabel(m.mediaType);
                      return (
                        <li
                          key={m.id}
                          className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/90"
                        >
                          {media && (
                            <span className="mr-1.5 inline-block rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                              {media}
                            </span>
                          )}
                          {m.text}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
